import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, useDragControls } from 'framer-motion';
import { acquireScrollLock, releaseScrollLock } from '@/lib/scrollLock';
import s from './BottomSheet.module.css';
import { Icon } from './Icon';
// Tuned to feel close to the iOS sheet — overdrag past 120px or fling
// downward faster than 600px/s commits to a close.
const DRAG_DISMISS_OFFSET_PX = 120;
const DRAG_DISMISS_VELOCITY = 600;
/**
 * Bottom-anchored modal sheet ported from the opengater LK `tariff-modal`
 * pattern: spring-physics slide-in/out, drag-to-dismiss via a visible
 * handle area at the top. Backdrop fades independently of the sheet.
 *
 * **Exit animation requires an `<AnimatePresence>` wrapper at the consumer
 * level.** The exit keyframes (backdrop fade, sheet slide-down) are declared
 * on the motion components below, but they only run when AnimatePresence
 * detects the BottomSheet's unmount in the React tree. Conditional render
 * pattern at the parent:
 *
 *     <AnimatePresence>
 *       {viewRow && (
 *         <BottomSheet title="..." onClose={...}>...</BottomSheet>
 *       )}
 *     </AnimatePresence>
 *
 * Internal AnimatePresence used to gate `open && (...)` here, but parents
 * already gate the entire BottomSheet via `{condition && <BottomSheet/>}` —
 * meaning the internal AnimatePresence never saw the open→closed flip, it
 * was unmounted together with its parent before having a chance to play.
 *
 * Drag behaviour mirrors the LK reference exactly:
 *   - The sheet sets `dragListener={false}` so the body content can scroll
 *     freely; the only thing that initiates a drag is the handle area
 *     calling `dragControls.start(event)` on pointerdown.
 *   - `dragElastic.bottom = 0.4` lets you pull a bit past the natural
 *     resting position before resistance kicks in.
 *   - On drag end, if you crossed the offset/velocity threshold the sheet
 *     calls `onClose`; otherwise framer's spring snaps it back to 0.
 *
 * The DOM testid `bottom-sheet-overlay` is preserved so the existing test
 * (BottomSheet.test.tsx) keeps working.
 */
export function BottomSheet({ open = true, title, subtitle, onClose, footer, children, }) {
    const sheetRef = useRef(null);
    const dragControls = useDragControls();
    // useLayoutEffect (not useEffect) so the scrollbar compensation lands
    // BEFORE the browser paints the sheet's enter frame — otherwise there
    // is a 1-frame flash where the page is still scrollable and shifts as
    // overflow:hidden kicks in.
    useLayoutEffect(() => {
        if (!open)
            return;
        acquireScrollLock();
        return releaseScrollLock;
    }, [open]);
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        // Focus the sheet so screen readers announce the dialog and keyboard
        // users land inside it (proper focus-trap is a follow-up).
        sheetRef.current?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);
    const handleDragEnd = (_, info) => {
        if (info.offset.y > DRAG_DISMISS_OFFSET_PX ||
            info.velocity.y > DRAG_DISMISS_VELOCITY) {
            onClose();
        }
    };
    // Back-compat shortcut: `<BottomSheet open={false}>` renders nothing, same
    // as the pre-animation behaviour. New code paths should omit the prop and
    // wrap the conditional in <AnimatePresence> instead.
    if (!open)
        return null;
    return createPortal(_jsxs("div", { className: s.root, role: "dialog", "aria-modal": "true", children: [_jsx(motion.div, { className: s.overlay, "data-testid": "bottom-sheet-overlay", onClick: onClose, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.28, ease: 'easeOut' } }), _jsxs(motion.div, { className: s.sheet, ref: sheetRef, tabIndex: -1, initial: { y: '100%' }, animate: { y: 0 }, exit: { y: '100%' }, transition: { type: 'spring', damping: 30, stiffness: 240, mass: 0.95 }, drag: "y", dragControls: dragControls, dragListener: false, dragConstraints: { top: 0, bottom: 0 }, dragElastic: { top: 0, bottom: 0.4 }, onDragEnd: handleDragEnd, children: [_jsx("div", { className: s.handleArea, onPointerDown: (e) => dragControls.start(e), "aria-hidden": "true", children: _jsx("div", { className: s.handle }) }), _jsxs("div", { className: s.head, children: [_jsxs("div", { children: [_jsx("div", { className: s.title, children: title }), subtitle && _jsx("div", { className: s.subtitle, children: subtitle })] }), _jsx("button", { type: "button", className: s.close, "aria-label": "Close", onClick: onClose, children: _jsx(Icon, { name: "x", size: 18 }) })] }), _jsx("div", { className: s.body, children: children }), footer && _jsx("div", { className: s.foot, children: footer })] })] }), document.body);
}
